import type { JSX } from 'react';
import { useState } from 'react';
import { SignupRequestSchema } from '@crv/contracts/auth';
import { Alert, Button, Link, Stack, TextField, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { NavLink, useNavigate } from 'react-router-dom';

import { ApiClientError } from '../api/http-client';
import { signup } from '../api/signup';
import { AuthShell } from './AuthShell';

type SignupFieldErrors = Partial<Record<'email' | 'password' | 'confirmPassword', string>>;

export function SignupPage(): JSX.Element {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<SignupFieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: signup,
    onSuccess: (_data, variables) => {
      const nextEmail = variables.email.trim();
      const nextUrl =
        nextEmail.length > 0 ? `/login?email=${encodeURIComponent(nextEmail)}` : '/login';
      navigate(nextUrl, { replace: true });
    },
    onError: (error) => {
      const message =
        error instanceof ApiClientError
          ? error.body?.message ?? error.message
          : error instanceof Error
            ? error.message
            : 'Unable to sign up. Please try again.';
      setFormError(message);

      if (error instanceof ApiClientError && error.body) {
        console.error('[signup] API error', {
          status: error.status,
          code: error.body.code,
          traceId: error.body.traceId,
        });
      } else {
        console.error('[signup] Unexpected error', error);
      }
    },
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setFieldErrors({});
    setFormError(null);

    const errors: SignupFieldErrors = {};

    const parsedRequest = SignupRequestSchema.safeParse({ email, password });
    if (!parsedRequest.success) {
      for (const issue of parsedRequest.error.issues) {
        const field = issue.path[0];
        if ((field === 'email' || field === 'password') && !errors[field]) {
          errors[field] = issue.message;
        }
      }
    }

    if (!confirmPassword.trim()) {
      errors.confirmPassword = errors.confirmPassword ?? 'Confirm your password.';
    }

    if (!errors.password && password.trim().length > 0 && password.length < 8) {
      errors.password = 'Password must be at least 8 characters.';
    }

    if (!errors.confirmPassword && password && confirmPassword && password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match.';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    mutation.mutate(parsedRequest.success ? parsedRequest.data : { email, password });
  };

  return (
    <AuthShell
      title="Create your account"
      subtitle="Start a CRV Systems workspace, then sign in to continue."
    >
      <form onSubmit={handleSubmit} noValidate>
        <Stack spacing={2.5}>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
            autoComplete="email"
            autoFocus
            required
            fullWidth
            error={Boolean(fieldErrors.email)}
            helperText={fieldErrors.email ?? ' '}
          />

          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
            autoComplete="new-password"
            required
            fullWidth
            error={Boolean(fieldErrors.password)}
            helperText={fieldErrors.password ?? 'Minimum 8 characters.'}
          />

          <TextField
            label="Confirm password"
            type="password"
            value={confirmPassword}
            onChange={(event) => {
              setConfirmPassword(event.target.value);
            }}
            autoComplete="new-password"
            required
            fullWidth
            error={Boolean(fieldErrors.confirmPassword)}
            helperText={fieldErrors.confirmPassword ?? ' '}
          />

          {formError && <Alert severity="error">{formError}</Alert>}

          <Button type="submit" variant="contained" size="large" disabled={mutation.isPending}>
            {mutation.isPending ? 'Creating account...' : 'Create account'}
          </Button>

          <Typography variant="body2" color="text.secondary">
            Already have an account?{' '}
            <Link component={NavLink} to="/login" underline="hover">
              Sign in
            </Link>
          </Typography>
        </Stack>
      </form>
    </AuthShell>
  );
}
